import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, XStack } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { handleExternalUrlClick } from '@/utils/platform';

import { chatDebugStore } from './chat-debug.store';

const formatTimestamp = (isoString: string) => {
  try {
    const dateObj = parseISO(isoString);
    return format(dateObj, "do MMMM hh:mm:ss's'");
  } catch {
    return isoString;
  }
};

const MessageList = observer(() => {
  const session = chatDebugStore.active;
  const messages = useMemo(() => session?.messages ?? [], [session?.messages]);
  const activeMessageId = session?.activeMessageId ?? null;

  const selectedMessage = useMemo(() => {
    return messages.find((message) => message.id === activeMessageId);
  }, [activeMessageId, messages]);

  const handleMessageChange = (messageId: string) => {
    if (!session || messageId === activeMessageId) return;
    session.setActiveMessageId(messageId);
    const message = messages.find((m) => m.id === messageId);
    if (message?.traceId) {
      session.fetchTraceForMessage(message.traceId);
    }
  };

  if (!session || !activeMessageId || messages.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        variant="ghost"
        className="group min-w-0 flex-1 h-auto justify-between rounded-md border border-border"
      >
        <div className="flex flex-col gap-0.5 overflow-hidden text-left w-full">
          {selectedMessage && (
            <div className="flex flex-col gap-1">
              <div className="text-sm line-clamp-2 text-foreground">
                {selectedMessage.text}
              </div>
            </div>
          )}
        </div>
        <Icons.chevronDown className="h-4 w-4 text-foreground-subtle group-data-[state=open]:rotate-180 transition-transform" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="start" className="w-[340px] p-1">
        {messages.map((message) => (
          <DropdownMenu.Item
            key={message.id}
            onClick={() => handleMessageChange(message.id)}
            data-selected={message.id === activeMessageId}
            className="flex-col items-start gap-2 py-2"
          >
            <p className="text-xs text-foreground truncate w-full">
              {message.text}
            </p>
            <XStack className="w-full gap-4 justify-between items-center">
              <p className="font-medium text-sm text-foreground-muted">
                {message.agent}
              </p>
              <XStack className="gap-2 items-center">
                <Icons.clock className="h-3 w-3 text-foreground-subtle" />
                <span className="text-xs text-foreground-subtle">
                  {formatTimestamp(message.createdAt.toISOString())}
                </span>
              </XStack>
            </XStack>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

export const ChatDebugHeader = observer(() => {
  const session = chatDebugStore.active;
  const messages = useMemo(() => session?.messages ?? [], [session?.messages]);
  const activeMessageId = session?.activeMessageId ?? null;

  const activeMessage = useMemo(() => {
    return messages.find((message) => message.id === activeMessageId) ?? null;
  }, [messages, activeMessageId]);

  if (!session) return null;

  const handleOpenTrace = () => {
    if (!activeMessage?.trace_url) return;
    handleExternalUrlClick(activeMessage.trace_url);
  };

  return (
    <XStack className="gap-2 px-3 pb-2 justify-between items-center min-w-0">
      <XStack className="flex-1 min-w-0">
        <MessageList />
      </XStack>
      <XStack className="gap-1 items-center shrink-0">
        <IconButton
          size="sm"
          icon={Icons.externalLink}
          onClick={handleOpenTrace}
          disabled={!activeMessage?.trace_url}
          tooltip="Open trace externally"
        />
        <IconButton
          size="sm"
          icon={Icons.refresh}
          onClick={() => session.fetchTracesForChat()}
          disabled={session.loadingTracesForChat}
          tooltip="Refresh traces"
          className={session.loadingTracesForChat ? '[&_svg]:animate-spin' : ''}
        />
        <IconButton
          size="sm"
          icon={Icons.download}
          onClick={() => session.exportDebugData()}
          disabled={
            session.exportingDebugData ||
            session.loadingTracesForChat ||
            messages.length === 0
          }
          tooltip="Download debug data (JSON)"
          className={session.exportingDebugData ? '[&_svg]:animate-spin' : ''}
        />
      </XStack>
    </XStack>
  );
});
